/** @format */

import {useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {PasswordRecovery as PasswordRecoveryScreen, type RecoveryMode} from 'ui';
import {useSearchParams, secondLevelDomain} from 'helpers/routerHelpers';
import {TELEGRAM_SUPPORT_URL} from 'components/fabs/components/HelpFab/constants';

import {useResetPasswordMutation} from './graphql/__generated__';
import {useResetPasswordEmailMutation} from '../ResetPasswordEmail/graphql/__generated__';

const ResetPassword = () => {
  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const {t} = useTranslation();

  const initialMode: RecoveryMode = searchParams.get('mode') === 'email' ? 'mail' : 'phone';
  const [mode, setMode] = useState<RecoveryMode>(initialMode);
  const [error, setError] = useState<string>();
  const [sentState, setSentState] = useState<'idle' | 'success'>('idle');
  const [troubleshootingOpen, setTroubleshootingOpen] = useState(false);

  const [resetPhone, {loading: phoneLoading}] = useResetPasswordMutation();
  const [resetEmail, {loading: emailLoading}] = useResetPasswordEmailMutation();

  const submitState = phoneLoading || emailLoading ? 'submitting' : 'idle';
  // A second submit while the first is in flight would send a duplicate
  // SMS/email (Apollo is no-cache, so there's no request de-dup). The button's
  // disabled state is a render behind the mutate() call; a synchronous ref
  // closes that gap.
  const submittingRef = useRef(false);

  const handleModeChange = (next: RecoveryMode) => {
    setMode(next);
    setError(undefined);
    const search = next === 'mail' ? '?mode=email' : '';
    navigate({pathname: '/auth/reset-password', search}, {replace: true});
  };

  const handleSubmit = async ({mode: submitMode, value}: {mode: RecoveryMode; value: string}) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError(undefined);
    try {
      // NumberFormat adds trailing spaces (phone), trim both for consistency.
      // Both mutations return a Boolean — only a `true` result means the reset
      // actually went out; a soft `false` must not read as success.
      const ok =
        submitMode === 'phone'
          ? (await resetPhone({variables: {phone: value.trim()}})).data?.resetPassword
          : (await resetEmail({variables: {email: value.trim()}})).data?.resetPasswordEmail;
      if (ok) {
        setSentState('success');
      } else {
        setError(
          t(
            'Не удалось отправить пароль, попробуйте ещё раз',
            'Не удалось отправить пароль, попробуйте ещё раз',
          ),
        );
      }
    } catch (e) {
      if (e instanceof Error) setError(e.message);
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <PasswordRecoveryScreen
      mode={mode}
      serverError={error}
      sentState={sentState}
      submitState={submitState}
      troubleshootingOpen={troubleshootingOpen}
      onModeChange={handleModeChange}
      onSubmit={handleSubmit}
      onBackClick={() => navigate('/auth/sign-in')}
      onBackToSignInClick={() => navigate('/auth/sign-in')}
      onTroubleshootingClick={() => setTroubleshootingOpen(true)}
      onTroubleshootingClose={() => setTroubleshootingOpen(false)}
      onTroubleshootingRetry={() => {
        // «Попробовать ещё» — закрыть модалку и вернуть форму для повторной отправки.
        setTroubleshootingOpen(false);
        setSentState('idle');
        setError(undefined);
      }}
      onContactSupport={() => window.open(TELEGRAM_SUPPORT_URL, '_blank', 'noopener')}
      onLogoClick={() => window.location.assign(`https://${secondLevelDomain()}/`)}
    />
  );
};

export default ResetPassword;
